import { useState } from "react";
import { api, type Organization } from "../api/client";
import { type DecryptedItem, type Field } from "../vault/items";
import { templateFor } from "../vault/catalog";
import { type EditorState } from "./ItemEditor";
import { AttachmentsPanel } from "./AttachmentsPanel";
import { openExternal } from "../desktop/bridge";
import { toggleFavorite } from "../vault/prefs";
import { type FolderNode } from "./folder-tree";
import { copyEphemeral } from "./clipboard";
import { useToast } from "./toast-context";
import { Icons } from "./icons";
import { useT } from "../i18n";

type DetailsPaneProps = {
  selected: DecryptedItem | null;
  mode: "org" | "shared" | "trash";
  readOnly: boolean;
  selectedOrg: Organization | null;
  orgKeyFor: (org: Organization) => Uint8Array;
  folders: FolderNode[];
  favorites: Set<string>;
  setFavorites: (next: Set<string>) => void;
  breakGlass: boolean;
  canLaunch: (it: DecryptedItem) => boolean;
  launchItem: (it: DecryptedItem) => boolean;
  launchLabel: (it: DecryptedItem) => string;
  setEditor: (e: EditorState) => void;
  duplicateItem: (it: DecryptedItem) => Promise<void>;
  trashItem: (it: DecryptedItem) => Promise<void>;
  restoreFromTrash: () => Promise<void>;
  setSharing: (id: string) => void;
  shareOnce: (it: DecryptedItem) => void;
  setHistoryId: (id: string) => void;
  moveSelected: (folderId: string) => Promise<void>;
};

/** Right-hand "PROPERTIES" pane: per-item actions, favorite/move, decrypted fields, attachments. */
export function DetailsPane({
  selected,
  mode,
  readOnly,
  selectedOrg,
  orgKeyFor,
  folders,
  favorites,
  setFavorites,
  breakGlass,
  canLaunch,
  launchItem,
  launchLabel,
  setEditor,
  duplicateItem,
  trashItem,
  restoreFromTrash,
  setSharing,
  shareOnce,
  setHistoryId,
  moveSelected,
}: DetailsPaneProps) {
  const t = useT();
  return (
    <aside className="flex w-72 flex-col border-l border-border bg-panel">
      <div className="eyebrow px-3 py-2">{t("PROPERTIES")}</div>
      <div className="px-3 py-2 text-xs">
        {selected ? (
          <div className="flex flex-col gap-2">
            {mode === "trash" && (
              <button onClick={restoreFromTrash} className="rounded bg-accent px-2 py-1 text-white hover:bg-accent2">
                {t("Restore from trash")}
              </button>
            )}
            {mode !== "trash" && (
              <div className="flex flex-wrap items-center gap-1">
                {canLaunch(selected) && (
                  <button onClick={() => launchItem(selected)} className="rounded bg-accent px-2 py-1 font-medium text-white hover:bg-accent2">
                    ▶ {launchLabel(selected)}
                  </button>
                )}
                {mode !== "shared" && !readOnly && (
                  <button onClick={() => setEditor({ mode: "edit", itemId: selected.id })} className="rounded border border-border px-2 py-1 hover:border-accent">
                    {t("Edit")}
                  </button>
                )}
                {mode === "org" && !readOnly && (
                  <button onClick={() => void duplicateItem(selected)} className="rounded border border-border px-2 py-1 hover:border-accent">
                    {t("Duplicate")}
                  </button>
                )}
                {mode === "org" && !readOnly && (
                  <button onClick={() => setSharing(selected.id)} className="rounded border border-border px-2 py-1 hover:border-accent">
                    {t("Share")}
                  </button>
                )}
                <button onClick={() => shareOnce(selected)} className="rounded border border-border px-2 py-1 hover:border-accent" title={t("One-time link, shown once then destroyed")}>
                  {t("Share once…")}
                </button>
                {mode === "org" && !readOnly && (
                  <button onClick={() => setHistoryId(selected.id)} className="rounded border border-border px-2 py-1 hover:border-accent">
                    {t("History")}
                  </button>
                )}
                {mode !== "shared" && !readOnly && (
                  <button onClick={() => void trashItem(selected)} className="rounded border border-border px-2 py-1 text-danger hover:border-danger">
                    {t("Delete")}
                  </button>
                )}
              </div>
            )}
            {mode === "org" && !readOnly && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFavorites(toggleFavorite(selected.id))}
                  className={favorites.has(selected.id) ? "text-warn" : "text-muted hover:text-fg"}
                  title="Favorite"
                >
                  {favorites.has(selected.id) ? "★ Favorite" : "☆ Favorite"}
                </button>
                <select
                  value={selected.folderId ?? ""}
                  onChange={(e) => moveSelected(e.target.value)}
                  title="Move to folder"
                  className="ml-auto rounded border border-border bg-panel2 px-1.5 py-0.5 text-[11px]"
                >
                  <option value="">{t("(root)")}</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {"— ".repeat(f.depth)}
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <dl className="space-y-2">
              <Prop k={t("Name")} v={selected.content.name} />
              <Prop k={t("Type")} v={templateFor(selected.type).label} />
              {selected.content.fields.filter((f) => f.value).map((f, i) => (
                <FieldDisplay key={i} field={f} itemId={selected.id} breakGlass={breakGlass} />
              ))}
              {(selected.content.tags?.length ?? 0) > 0 && <Prop k={t("Tags")} v={(selected.content.tags ?? []).join(", ")} />}
              {selected.content.notes && <Prop k={t("Notes")} v={selected.content.notes} />}
            </dl>
            {mode === "org" && selectedOrg && (
              <AttachmentsPanel itemId={selected.id} orgKey={orgKeyFor(selectedOrg)} canEdit={true} />
            )}
          </div>
        ) : (
          <span className="text-muted">{t("No selection.")}</span>
        )}
      </div>
    </aside>
  );
}

function Prop({ k, v, copy }: { k: string; v: string; copy?: boolean }) {
  const toast = useToast();
  const t = useT();
  return (
    <div className="flex flex-col">
      <dt className="text-muted">{k}</dt>
      <dd className="flex items-center gap-2 break-all text-fg">
        {v}
        {copy && (
          <button
            onClick={() => {
              navigator.clipboard.writeText(v).catch(() => undefined);
              toast.success(`${k} — ${t("copied")}`);
            }}
            className="text-accent hover:underline"
          >
            copy
          </button>
        )}
      </dd>
    </div>
  );
}

// Best-effort field-level audit (§16). For break-glass users a reason is required for secret
// reveal/copy; otherwise it's optional. Returns false if the user cancelled a required reason.
function auditField(itemId: string, field: Field, kind: "field_revealed" | "field_copied", breakGlass: boolean): boolean {
  let reason: string | undefined;
  if (breakGlass && (field.secret ?? false)) {
    const r = window.prompt("Reason (required, audited):");
    if (!r) return false;
    reason = r;
  }
  void api.recordEvent({ event_type: kind, item_id: itemId, detail: field.key, reason }).catch(() => undefined);
  return true;
}

// Renders one decrypted field by type: secrets reveal/copy, TOTP shows a live code.
function FieldDisplay({ field, itemId, breakGlass }: { field: Field; itemId: string; breakGlass: boolean }) {
  const [shown, setShown] = useState(false);
  const toast = useToast();
  const t = useT();
  const isSecret = field.secret ?? false;
  // Copy belongs only on credentials (username + secrets/passwords) — not on connection
  // parameters like resolution, color depth, ports or gateway options.
  const canCopy = isSecret || field.type === "username";
  const copy = () => {
    if (auditField(itemId, field, "field_copied", breakGlass)) {
      copyEphemeral(field.value);
      toast.success(`${field.label} — ${t("copied")}`);
    }
  };
  const toggleReveal = () => {
    if (shown) {
      setShown(false);
      return;
    }
    if (auditField(itemId, field, "field_revealed", breakGlass)) setShown(true);
  };

  if (field.type === "url") {
    return (
      <div className="flex flex-col">
        <dt className="text-muted">{field.label}</dt>
        <dd>
          <a
            href={field.value}
            onClick={(e) => {
              e.preventDefault();
              void openExternal(field.value);
            }}
            className="cursor-pointer break-all text-accent hover:underline"
          >
            {field.value}
          </a>
        </dd>
        {field.note && <div className="text-[11px] text-muted">{field.note}</div>}
      </div>
    );
  }

  const multiline = field.multiline ?? false;
  const display = isSecret && !shown ? "••••••••" : field.value;
  return (
    <div className="flex flex-col">
      <dt className="flex items-center gap-1 text-muted">{field.label}{isSecret ? <Icons.lock className="h-3 w-3" /> : null}</dt>
      <dd className="flex items-start gap-2">
        {multiline ? (
          <pre className="max-h-40 flex-1 overflow-auto whitespace-pre-wrap break-all rounded bg-panel2 px-2 py-1 font-mono text-[11px]">{isSecret && !shown ? "••••••••" : field.value}</pre>
        ) : (
          <span className="flex-1 break-all font-mono">{display}</span>
        )}
        <span className="flex shrink-0 items-center gap-1.5">
          {isSecret && (
            <button onClick={toggleReveal} aria-label={shown ? "hide" : "reveal"} className="text-muted hover:text-accent">
              {shown ? <Icons.eyeOff className="h-3.5 w-3.5" /> : <Icons.eye className="h-3.5 w-3.5" />}
            </button>
          )}
          {canCopy && (
            <button onClick={copy} aria-label="copy" className="text-muted hover:text-accent">
              <Icons.copy className="h-3.5 w-3.5" />
            </button>
          )}
        </span>
      </dd>
      {field.note && <div className="text-[11px] text-muted">{field.note}</div>}
    </div>
  );
}
